import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { axiosInstance } from "../../../utils/axios";
import PaginationV2 from "../../../components/PaginationV2";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { XMarkIcon, SwatchIcon } from "@heroicons/react/20/solid";
import ColumnSearch from "../../../components/ColumnSearch";
import ActionButtons from "../../../components/ActionButtons";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";
import RentDeviceModal from "./RentDeviceModal";
import RentMultipleDevices from "../../../components/RentMultipleDevices";
import useModalStore from "../../../stores/useModalStore";
import useDialogStore from "../../../stores/useDialogStore";
import EditableCell from "../../../components/EditableCell";

export default function RentDevices() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showRentalModal, setShowRentalModal } = useModalStore();
  const { openDialog } = useDialogStore();
  const [activeDevice, setActiveDevice] = useState(null);

  const sortConfig = {
    key: null,
    direction: "desc",
  };

  const {
    data: devicesData,
    isLoading,
  } = useQuery({
    queryKey: ["devices-not-rented", location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals/devices-not-rented?${params.toString()}`
      );
      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
    cacheTime: 0,
  });

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page");
    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["devices-not-rented"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["devices-not-rented"]);
  };

  const handleClearFilters = () => {
    navigate(location.pathname);
    queryClient.invalidateQueries(["devices-not-rented"]);
  };

  const editCellMutation = useMutation({
    mutationFn: ({ id, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/devices/patch/${id}`,
        { [field]: value }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["devices-not-rented"]);
      toast.success("Device updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
      console.error("Update device error:", error);
    },
  });

  const handleRentDevice = (device) => {
    setActiveDevice(device);
    setShowRentalModal(true);
  };

  const openMultipleRentModal = () => {
    openDialog({
      title: "Rent Multiple Devices by Unique ID",
      content: <RentMultipleDevices />,
    });
  };

  const devices = devicesData?.data || [];
  const paginationData = {
    current_page: devicesData?.current_page || 1,
    last_page: devicesData?.last_page || 1,
    next_page_url: devicesData?.next_page_url,
    prev_page_url: devicesData?.prev_page_url,
  };

  return (
    <div className="px-6 py-4">
      <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-gray-50 rounded-lg mb-4">
        <div className="flex items-center lg:flex-row flex-col gap-2">
          <Button
            variant="secondary"
            size="sm"
            icon={<XMarkIcon className="h-4 w-4" />}
            onClick={handleClearFilters}
            className="mb-2 lg:mb-0"
          >
            Clear Filters
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<SwatchIcon className="h-4 w-4" />}
            onClick={openMultipleRentModal}
            className="mb-2 lg:mb-0"
          >
            + Rent Multiple Devices by Unique ID
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-black">
                <tr>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Unique ID"
                      columnKey="unique_id"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search by Unique ID..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Brand"
                      columnKey="brand"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search by Brand..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Model"
                      columnKey="model"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search by Model..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Serial No"
                      columnKey="serial_no"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search by Serial No..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Counter Black"
                      columnKey="counter_black"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Counter Color"
                      columnKey="counter_color"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm">
                    <ColumnSearch
                      columnName="Status"
                      columnKey="status"
                      onSearch={handleColumnSearch}
                      onSort={handleColumnSort}
                      sortConfig={sortConfig}
                      placeholder="Search by Status..."
                    />
                  </th>
                  <th className="px-4 py-4 text-left text-white text-sm min-w-[200px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="text-center">
                      <LoadingComponent />
                    </td>
                  </tr>
                ) : devices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center">
                      <NoData message="No available devices for rent" />
                    </td>
                  </tr>
                ) : (
                  devices.map((device, index) => (
                    <tr
                      key={device.id}
                      className={`divide-y divide-gray-200 ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-100"
                      }`}
                    >
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {device.unique_id || "---"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={device.device_brand?.name || ""}
                          displayValue={device.device_brand?.name || "---"}
                          id={device.id}
                          field="device_brand_id"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "device_brand_id",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={device.device_model?.name || ""}
                          displayValue={device.device_model?.name || "---"}
                          id={device.id}
                          field="device_model_id"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "device_model_id",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={device.serial_no || ""}
                          displayValue={device.serial_no || "---"}
                          id={device.id}
                          field="serial_no"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "serial_no",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={device.counter_black || ""}
                          displayValue={device.counter_black || "---"}
                          id={device.id}
                          field="counter_black"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "counter_black",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <EditableCell
                          value={device.counter_color || ""}
                          displayValue={device.counter_color || "---"}
                          id={device.id}
                          field="counter_color"
                          onSave={(data) =>
                            editCellMutation.mutate({
                              id: data.id,
                              field: "counter_color",
                              value: data.value,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                          {device.status || "Available"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-900 flex items-center gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleRentDevice(device)}
                        >
                          + Rent Device
                        </Button>
                        <ActionButtons
                          id={device.id}
                          onDelete={() => {}}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
      </div>
      <PaginationV2
        current_page={paginationData.current_page}
        last_page={paginationData.last_page}
        next_page_url={paginationData.next_page_url}
        prev_page_url={paginationData.prev_page_url}
      />
      {showRentalModal && (
        <RentDeviceModal
          device={activeDevice}
          onSuccess={() => {
            queryClient.invalidateQueries(["devices-not-rented"]);
            setShowRentalModal(false);
          }}
        />
      )}
    </div>
  );
}